import { base64ToUint8, uint8ToBase64 } from './encoding';

const IDB_NAME = 'sariz-notes-ydoc';
const IDB_STORE = 'docs';
const IDB_VERSION = 1;

function cacheKey(workspaceId: string, filePath: string): string {
  return `${workspaceId}::${filePath}`;
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
  });
}

function idbGet(key: string): Promise<unknown> {
  return openIdb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).get(key);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      }),
  );
}

function idbSet(key: string, value: unknown): Promise<void> {
  return openIdb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(value, key);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      }),
  );
}

export async function readLocalFileYdoc(
  workspaceId: string,
  filePath: string,
): Promise<{ bytes: Uint8Array; dirty: boolean } | null> {
  if (typeof window === 'undefined') return null;
  try {
    const row = (await idbGet(cacheKey(workspaceId, filePath))) as {
      data?: ArrayBuffer | ArrayBufferView;
      dirty?: boolean;
    } | null;
    if (row?.data instanceof ArrayBuffer) {
      const bytes = new Uint8Array(row.data);
      if (bytes.length) return { bytes, dirty: !!row.dirty };
    }
    if (row?.data && ArrayBuffer.isView(row.data)) {
      const bytes = new Uint8Array(
        row.data.buffer,
        row.data.byteOffset,
        row.data.byteLength,
      );
      if (bytes.length) return { bytes, dirty: !!row.dirty };
    }
  } catch {
    // ignore
  }
  return null;
}

export function writeLocalFileYdoc(
  workspaceId: string,
  filePath: string,
  bytes: Uint8Array,
  dirty: boolean,
): Promise<void> {
  if (typeof window === 'undefined' || !bytes?.length) {
    return Promise.resolve();
  }
  return idbSet(cacheKey(workspaceId, filePath), {
    data: bytes.slice(0),
    dirty: !!dirty,
  }).catch(() => {
    try {
      localStorage.setItem(
        `sariz-notes-ydoc:${cacheKey(workspaceId, filePath)}`,
        JSON.stringify({ data: uint8ToBase64(bytes), dirty: !!dirty }),
      );
    } catch (e) {
      console.warn('Failed to save ydoc locally', e);
    }
  });
}

export async function clearLocalFileYdoc(
  workspaceId: string,
  filePath: string,
): Promise<void> {
  if (typeof window === 'undefined') return;
  const key = cacheKey(workspaceId, filePath);
  try {
    const db = await openIdb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(key);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(`sariz-notes-ydoc:${key}`);
  } catch {
    // ignore
  }
}

/**
 * Drop every locally cached ydoc for one workspace (IndexedDB plus the legacy
 * localStorage fallback). Used when the workspace itself is deleted so no
 * note content outlives the deletion on this device.
 */
export async function clearLocalWorkspaceYdocs(
  workspaceId: string,
): Promise<void> {
  if (typeof window === 'undefined') return;
  const prefix = `${workspaceId}::`;

  try {
    const db = await openIdb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const request = store.getAllKeys();
      // Issue the deletes synchronously inside the request callback while the
      // transaction is still active.
      request.onsuccess = () => {
        for (const key of request.result) {
          if (typeof key === 'string' && key.startsWith(prefix)) {
            store.delete(key);
          }
        }
      };
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch {
    // ignore
  }

  try {
    const legacyKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`sariz-notes-ydoc:${prefix}`)) {
        legacyKeys.push(key);
      }
    }
    for (const key of legacyKeys) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

export function readLegacyLocalStorage(
  workspaceId: string,
  filePath: string,
): { bytes: Uint8Array; dirty: boolean } | null {
  try {
    const raw = localStorage.getItem(
      `sariz-notes-ydoc:${cacheKey(workspaceId, filePath)}`,
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data?: string; dirty?: boolean };
    if (!parsed?.data) return null;
    const bytes = base64ToUint8(parsed.data);
    if (!bytes?.length) return null;
    return { bytes, dirty: !!parsed.dirty };
  } catch {
    return null;
  }
}
